import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

interface AttachmentItem {
  url: string;
  name: string;
  mime: string;
  ext: string;
}

interface AttachmentViewerProps {
  open: boolean;
  onClose: () => void;
  attachments: AttachmentItem[];
  initialIndex?: number;
  apiBaseUrl: string;
}

const PDFRenderer: React.FC<{ url: string; onDownload: () => void }> = ({ url, onDownload }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const pdfRef = useRef<any>(null);

  const loadPDF = async () => {
    try {
      setLoading(true);
      const pdf = await pdfjsLib.getDocument(url).promise;
      pdfRef.current = pdf;
      setNumPages(pdf.numPages);
      await renderPage(1);
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderPage = async (pageNumber: number) => {
    if (!pdfRef.current || !canvasRef.current) return;

    try {
      setLoading(true);
      const page = await pdfRef.current.getPage(pageNumber);
      const scale = 1.5;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
      setPageNum(pageNumber);
    } catch (error) {
      console.error('Error rendering page:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPDF();
  }, [url]);

  const goToPrevPage = async () => {
    if (pageNum > 1) {
      await renderPage(pageNum - 1);
    }
  };

  const goToNextPage = async () => {
    if (numPages && pageNum < numPages) {
      await renderPage(pageNum + 1);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
      {loading && <CircularProgress sx={{ color: '#fff' }} />}
      <canvas
        ref={canvasRef}
        style={{
          maxWidth: '100%',
          maxHeight: '60vh',
          border: '1px solid #444',
          borderRadius: '4px',
        }}
      />

      {numPages && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
          <IconButton
            onClick={goToPrevPage}
            disabled={pageNum === 1}
            sx={{ color: pageNum === 1 ? '#666' : '#fff' }}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Typography>
            Page {pageNum} of {numPages}
          </Typography>
          <IconButton
            onClick={goToNextPage}
            disabled={pageNum === numPages}
            sx={{ color: pageNum === numPages ? '#666' : '#fff' }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      )}

      <Button
        startIcon={<GetAppIcon />}
        onClick={onDownload}
        sx={{
          color: '#fff',
          borderColor: '#fff',
          '&:hover': { borderColor: '#aaa', color: '#aaa' },
        }}
        variant="outlined"
      >
        Download
      </Button>
    </Box>
  );
};

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  open,
  onClose,
  attachments,
  initialIndex = 0,
  apiBaseUrl,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, open]);

  const currentAttachment = attachments[currentIndex];
  const isImage = currentAttachment?.mime?.startsWith('image/');
  const isPdf = currentAttachment?.mime === 'application/pdf';
  const fullUrl = `${apiBaseUrl}${currentAttachment?.url}`;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? attachments.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === attachments.length - 1 ? 0 : prev + 1));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = currentAttachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogContent
        sx={{
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 0,
          minHeight: '80vh',
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#fff',
            zIndex: 10,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Main Content Area */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            width: '100%',
            p: 2,
            position: 'relative',
            backgroundColor: '#1a1a1a',
          }}
        >
          {isImage ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                component="img"
                src={fullUrl}
                alt={currentAttachment.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
              <Button
                startIcon={<GetAppIcon />}
                onClick={handleDownload}
                sx={{
                  color: '#fff',
                  borderColor: '#fff',
                  '&:hover': { borderColor: '#aaa', color: '#aaa' },
                }}
                variant="outlined"
              >
                Download
              </Button>
            </Box>
          ) : isPdf ? (
            <PDFRenderer url={fullUrl} onDownload={handleDownload} />
          ) : (
            <Typography sx={{ color: '#fff' }}>
              Unsupported file type: {currentAttachment.mime}
            </Typography>
          )}
        </Box>

        {/* Attachment Navigation (only show if multiple attachments) */}
        {attachments.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              p: 2,
              borderTop: '1px solid #333',
              backgroundColor: 'rgba(255,255,255,0.05)',
            }}
          >
            <IconButton
              onClick={handlePrevious}
              sx={{
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>
                {currentAttachment.name}
              </Typography>
              <Typography sx={{ color: '#aaa', fontSize: '0.75rem' }}>
                {currentIndex + 1} of {attachments.length}
              </Typography>
            </Box>

            <IconButton
              onClick={handleNext}
              sx={{
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentViewer;
